import { AuthContext } from '@urbanx/agx.auth.client';
import {
  AutopilotTaskDto,
  TaskCategoryStage,
  useAssignTaskToUser,
} from '@urbanx/agx.activities.client';
import { ContentLayout, PageLayout } from 'layout';
import { useContext, useEffect, useRef, useState } from 'react';
import './allTasks.scss';
import {
  AgxColumn,
  AgxRow,
  AgxTable,
  Icon_Clock,
  type IColumnConfig,
  type RowData,
} from '@urbanx/agx.ui-components2';
import useTaskSearch from './useTaskSearch';
import { useTaskCollectionChangeNotifier } from './useTaskCollectionChangeNotifier';
import TaskFilters from './TaskFilters/TaskFilters';
import { TaskFilterState } from './TaskFilters/TaskFiltersState';
import { useGetAssigneeOptions } from './TaskFilters/useGetAssigneeOptions';
import { useNavigateToTask } from 'utils/tasks/useNavigateToTask';
import { getExchangeDisplayTask, getExchangeTaskFamily } from 'utils/tasks/exchangeTaskIdentity';
import { TaskCell } from './Cells/TaskCell';
import TaskAgencyCell from './Cells/TaskAgencyCell';
import TaskCampaignCell from './Cells/TaskCampaignCell';
import TaskAssigneeCell from './Cells/TaskAssigneeCell';
import TaskStatusCell from './Cells/TaskStatusCell';
import TaskDueCell from './Cells/TaskDueCell';
import { useWorkflowConcurrencyError } from 'hooks/useWorkflowConcurrencyError';
import { useAgencyFilter } from 'hooks/useAgencyFilter';

const columnConfig: IColumnConfig[] = [
  { name: 'Task Type' },
  { name: 'Agency' },
  { name: 'Campaign' },
  {
    name: 'Assignee',
    align: 'center',
    onClick: (event) => event.stopPropagation(),
  },
  { name: 'Status' },
  { name: 'Due', align: 'right' },
];

const TASK_ROW_HEIGHT = 72;
const TASK_ROW_OVERSCAN = 8;

const shouldCollapseTask = (task: AutopilotTaskDto) =>
  task.category?.stage === TaskCategoryStage.AgreementCompliance ||
  task.category?.stage === TaskCategoryStage.ContractCompliance ||
  getExchangeTaskFamily(task.category?.taskType) !== undefined;

const isExchangeTask = (task: AutopilotTaskDto) =>
  getExchangeTaskFamily(task.category?.taskType) !== undefined;

const getCollapsedTaskKey = (task: AutopilotTaskDto) =>
  [
    task.campaignId,
    task.contractId ?? '',
    getExchangeTaskFamily(task.category?.taskType) ?? task.category?.stage,
  ].join(':');

const collapseComplianceTasks = (tasks: AutopilotTaskDto[]): AutopilotTaskDto[] => {
  const result: AutopilotTaskDto[] = [];
  const seenTasks = new Map<string, number>();

  for (const task of tasks) {
    if (shouldCollapseTask(task) && task.campaignId) {
      const key = getCollapsedTaskKey(task);
      const existingIndex = seenTasks.get(key);

      if (existingIndex !== undefined) {
        if (isExchangeTask(task)) {
          result[existingIndex] =
            getExchangeDisplayTask([result[existingIndex], task]) ?? result[existingIndex];
        }

        continue;
      }

      seenTasks.set(key, result.length);

      result.push(task);
      continue;
    }

    result.push(task);
  }

  return result;
};

const TasksTable = () => {
  const [filters, setFilters] = useState<TaskFilterState | undefined>(undefined);
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const { userAccount } = useContext(AuthContext);

  const { assigneeOptions, assigneeOptionsPending } = useGetAssigneeOptions(
    filters?.assignedToUserIds
  );
  const {
    agencyOptions,
    effectiveAgencyIds,
    agencies,
    isPending: isAgencyFilterPending,
    isAgencyScopeUnavailable,
    hasNoMatchingAgencies,
  } = useAgencyFilter({ selectedValues: filters?.agencyIds });

  const effectiveFilters = filters ? { ...filters, agencyIds: effectiveAgencyIds } : undefined;
  const filtersPending = isAgencyFilterPending || assigneeOptionsPending;

  const { hasMore, totalCount, tasks, searchAllTasksPending, loadMore, refreshSearch } =
    useTaskSearch(
      effectiveFilters,
      filtersPending,
      hasNoMatchingAgencies,
      isAgencyScopeUnavailable
    );

  useTaskCollectionChangeNotifier();
  const assignmentWorkflowRef = useRef<AutopilotTaskDto | undefined>(undefined);
  const handleWorkflowConcurrencyError = useWorkflowConcurrencyError();

  const rows = collapseComplianceTasks(tasks);
  const userName = [userAccount?.firstName, userAccount?.lastName]
    .filter((namePart) => namePart != null && namePart.length > 0)
    .join(' ');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !searchAllTasksPending) {
          loadMore();
        }
      },
      { threshold: 1 }
    );
    const current = loaderRef.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
  }, [hasMore, loadMore, searchAllTasksPending]);

  const { assignTaskToUser, assignTaskToUserPending } = useAssignTaskToUser({
    onSuccess: () => {
      // TODO: add some sort of notification on success when design is ready
    },
    onError: (error) => {
      if (!assignmentWorkflowRef.current) return;

      void handleWorkflowConcurrencyError(error, assignmentWorkflowRef.current, {
        refresh: refreshSearch,
      });
    },
  });

  const onAssignTaskToUser = async (userId?: string, taskId?: string) => {
    if (!userId || !taskId || assignTaskToUserPending) return;

    const assignedTask = tasks.find((t) => t.id === taskId);
    if (!assignedTask) return;

    assignmentWorkflowRef.current = assignedTask;

    const siblingIds = shouldCollapseTask(assignedTask)
      ? tasks
          .filter(
            (t) =>
              t.campaignId === assignedTask.campaignId &&
              t.contractId === assignedTask.contractId &&
              getCollapsedTaskKey(t) === getCollapsedTaskKey(assignedTask) &&
              !!t.id
          )
          .map((t) => t.id!)
      : [taskId];

    for (const id of siblingIds) {
      await assignTaskToUser({ taskId: id, userId });
    }
  };

  const navigateToTask = useNavigateToTask();
  const agenciesForRows = agencies;
  const tableRows: RowData[] = rows.map((task, rowIdx) => ({
    dataTestId: task.id,
    'Task Type': <TaskCell task={task} index={rowIdx} isNew={!!task.isNew} />,
    Agency: (
      <TaskAgencyCell task={task} agencies={agenciesForRows} index={rowIdx} isNew={!!task.isNew} />
    ),
    Campaign: (
      <TaskCampaignCell
        propertyAddress={task.propertyAddress}
        index={rowIdx}
        isNew={!!task.isNew}
      />
    ),
    Assignee: (
      <TaskAssigneeCell
        task={task}
        assigneeOptions={assigneeOptions}
        onAssignTask={onAssignTaskToUser}
      />
    ),
    Status: <TaskStatusCell task={task} />,
    Due: <TaskDueCell task={task} />,
  }));

  return (
    <PageLayout
      agentName={userName}
      primaryPageTitle="All Tasks"
      pageIcon={
        <div className="iconContainer">
          <Icon_Clock height={28} width={28} color="var(--fg-white)" />
        </div>
      }
      header={
        <TaskFilters
          totalCount={totalCount}
          onFilterChange={setFilters}
          pending={assigneeOptionsPending || isAgencyFilterPending}
          agencyOptions={agencyOptions}
          assigneeOptions={assigneeOptions}
        />
      }
    >
      <ContentLayout>
        <AgxRow fill>
          <AgxColumn fill gap="spacing-xl">
            <AgxTable
              columns={columnConfig}
              rows={tableRows}
              variant={tableRows.length ? 'default' : 'minimal'}
              showLoadingState={
                (searchAllTasksPending || totalCount === undefined) && tableRows.length === 0
              }
              tableStyle="square"
              noDataLabel="No task found."
              noDataGlyphType="checklistSmile"
              virtualized={{
                rowHeight: TASK_ROW_HEIGHT,
                overscanRows: TASK_ROW_OVERSCAN,
              }}
              onRowClick={(rowIdx) => navigateToTask(rows[rowIdx])}
            />
            {/* Loader indicator */}
            <div ref={loaderRef} style={{ textAlign: 'center', padding: 16 }}>
              {searchAllTasksPending ? 'Loading...' : ''}
            </div>
          </AgxColumn>
        </AgxRow>
      </ContentLayout>
    </PageLayout>
  );
};

export default TasksTable;
